import { isAxiosError } from 'axios';

import axiosInstance from '~/common/helpers/axiosInstance';
import { Pagination } from '~/common/types/Pagination';
import { Deployments, HistoricType } from '~/entities/HistoryDeployment';

// ============================================================================
// Engine V3 Eligibility API
// ============================================================================

export type EngineV3EligibilityResponse = {
  eligible: boolean;
  nonMigratedComponents: string[];
};

export type CheckV3EligibilityParams = {
  realm: string;
  pipelineId: string;
};

export async function checkV3Eligibility({
  realm,
  pipelineId
}: CheckV3EligibilityParams): Promise<EngineV3EligibilityResponse> {
  const response = await axiosInstance.post<EngineV3EligibilityResponse>(
    `/runtime/realms/${encodeURIComponent(realm)}/deployments/v3-eligibility`,
    { pipelineId }
  );

  return response.data;
}

// ============================================================================
// Coordinator error handling
// ============================================================================

export class RuntimeServiceError extends Error {
  readonly code?: string;

  readonly status?: number;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = 'RuntimeServiceError';
    this.code = code;
    this.status = status;
  }
}

export const isMaximumAllowedValueError = (
  error: unknown
): error is RuntimeServiceError =>
  error instanceof RuntimeServiceError &&
  error.code === 'MAXIMUM_ALLOWED_VALUE';

type CoordinatorErrorBody = {
  message?: string;
  error?: string;
  description?: string;
};

const toRuntimeServiceError = (error: unknown): Error => {
  if (isAxiosError<CoordinatorErrorBody>(error)) {
    const status = error.response?.status;
    const data = error.response?.data;
    const message = data?.message ?? data?.error ?? data?.description;
    if (typeof message === 'string') {
      const code = message.includes('is above the maximum allowed value')
        ? 'MAXIMUM_ALLOWED_VALUE'
        : undefined;
      return new RuntimeServiceError(message, code, status);
    }
    if (status !== undefined) {
      return new RuntimeServiceError(error.message, undefined, status);
    }
  }
  return error instanceof Error ? error : new Error(String(error));
};

// ============================================================================
// Deploy History API
// ============================================================================

export type DeployHistoryParams = {
  realm: string;
  pipelineName: string;
  pipelineMajorVersion?: string;
  page: number;
  size?: number;
  signal?: AbortSignal;
};

export async function deployHistory({
  realm,
  pipelineName,
  pipelineMajorVersion,
  page,
  size = 20,
  signal
}: DeployHistoryParams): Promise<Pagination<HistoricType>> {
  try {
    const { data } = await axiosInstance.get<Pagination<HistoricType>>(
      `/runtime/realms/${encodeURIComponent(realm)}/deploy/${encodeURIComponent(pipelineName)}/history`,
      {
        params: { page, size, pipelineMajorVersion, environment: 'prod' },
        signal
      }
    );
    return data;
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
}

export type DeployHistoryDistinctParams = {
  realm: string;
  projectId: string;
  pipelineName: string;
  status: string[];
  page: number;
  signal?: AbortSignal;
};

export async function deployHistoryDistinct({
  realm,
  projectId,
  pipelineName,
  status,
  page,
  signal
}: DeployHistoryDistinctParams): Promise<Pagination<Deployments>> {
  try {
    const { data } = await axiosInstance.get<Pagination<Deployments>>(
      `/runtime/realms/${encodeURIComponent(realm)}/deploy/history/distinct`,
      {
        params: {
          page,
          projectId,
          pipelineName,
          lastStatus: status.join(','),
          environment: 'prod'
        },
        signal
      }
    );
    return data;
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
}

// ============================================================================
// Rollback / Revert API
// ============================================================================

export type RollbackDeployParams = {
  realm: string;
  deployment: string;
  environment: string;
  pipelineName: string;
  pipelineMajorVersion: string;
  signal?: AbortSignal;
};

const rollbackParams = ({
  environment,
  deployment,
  pipelineName,
  pipelineMajorVersion
}: RollbackDeployParams) => ({
  environment,
  deploymentId: deployment,
  pipelineName,
  pipelineMajorVersion
});

export async function rollbackDeploy(
  params: RollbackDeployParams
): Promise<unknown> {
  try {
    const { data } = await axiosInstance.patch<unknown>(
      `/runtime/realms/${encodeURIComponent(params.realm)}/deployments/rollback`,
      undefined,
      { params: rollbackParams(params), signal: params.signal }
    );
    return data;
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
}

export async function rollbackDeployDetails(
  params: RollbackDeployParams
): Promise<unknown> {
  try {
    const { data } = await axiosInstance.get<unknown>(
      `/runtime/realms/${encodeURIComponent(params.realm)}/deployments/rollback`,
      { params: rollbackParams(params), signal: params.signal }
    );
    return data;
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
}

export type RevertDeployParams = {
  realm: string;
  deployHistoryId: string;
  environment: string;
  signal?: AbortSignal;
};

export async function revertDeploy({
  realm,
  deployHistoryId,
  environment,
  signal
}: RevertDeployParams): Promise<unknown> {
  try {
    const { data } = await axiosInstance.post<unknown>(
      `/runtime/realms/${encodeURIComponent(realm)}/revert`,
      { deployHistoryId },
      { params: { environment }, signal }
    );
    return data;
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
}

// ============================================================================
// License API
// ============================================================================

export type LicenseStatus = {
  licenseModel: string;
  pipelineSubscriptionAndRtu: Record<string, unknown>;
  totalRealmLicenses: number;
  totalLicensesInUse: number;
  totalAvailableLicenses: number;
};

export type CheckLicenseResult = {
  hasAnalysis?: boolean;
  analysis?: unknown[];
};

export type LicenseStatusParams = {
  realm: string;
  environment: string;
  size?: string;
  replicas?: number;
  pipelineId: string;
  signal?: AbortSignal;
};

export async function licenseStatus({
  realm,
  environment,
  size,
  replicas,
  pipelineId,
  signal
}: LicenseStatusParams): Promise<LicenseStatus> {
  try {
    const { data } = await axiosInstance.get<LicenseStatus>(
      `/runtime/realms/${encodeURIComponent(realm)}/deployments/license/status`,
      { params: { environment, size, replicas, pipelineId }, signal }
    );
    return data;
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
}

export type CheckLicenseParams = {
  realm: string;
  pipelineId: string;
  size: string;
  replicas: number;
  replicaInstanceName: string;
  environment: string;
  signal?: AbortSignal;
};

export async function checkLicense({
  realm,
  pipelineId,
  size,
  replicas,
  replicaInstanceName,
  environment,
  signal
}: CheckLicenseParams): Promise<CheckLicenseResult> {
  try {
    const { data } = await axiosInstance.get<CheckLicenseResult>(
      `/runtime/realms/${encodeURIComponent(realm)}/deployments/${encodeURIComponent(pipelineId)}/checkLicense`,
      { params: { size, replicas, replicaInstanceName, environment }, signal }
    );
    return data;
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
}

// ============================================================================
// Pipeline Validation / Delete API
// ============================================================================

export type ValidatePipelineParams = {
  realm: string;
  pipelineId: string;
  environment: string;
  context?: string;
  signal?: AbortSignal;
};

export async function validatePipeline({
  realm,
  pipelineId,
  environment,
  context = 'deploy',
  signal
}: ValidatePipelineParams): Promise<unknown> {
  try {
    const { data } = await axiosInstance.get<unknown>(
      `/design/realms/${encodeURIComponent(realm)}/pipelines/${encodeURIComponent(pipelineId)}/check`,
      { params: { environment, context }, signal }
    );
    return data;
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
}

export type DeleteDeployParams = {
  realm: string;
  deployment: string;
  environment: string;
  signal?: AbortSignal;
};

export async function deleteDeploy({
  realm,
  deployment,
  environment,
  signal
}: DeleteDeployParams): Promise<void> {
  try {
    await axiosInstance.delete(
      `/runtime/realms/${encodeURIComponent(realm)}/deployments/${encodeURIComponent(deployment)}`,
      { params: { environment }, signal }
    );
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
}

// ============================================================================
// Configurations / Replica / Engine API
// ============================================================================

export type Configuration = {
  id?: string;
  name: string;
  description?: string;
  consumers?: number;
  actualConsumers?: number;
};

type ConfigurationsParams = {
  realm: string;
  pipelineId: string;
  environment: string;
  signal?: AbortSignal;
};

const CONFIGURATION_FIXED_ORDER = ['small', 'medium', 'large'];

const configurationPrefix = (name: string) =>
  name.split('-')[0]?.toLowerCase() ?? '';

const orderConfigurations = (data: Configuration[]): Configuration[] => {
  const fixed = CONFIGURATION_FIXED_ORDER.reduce<Configuration[]>(
    (ordered, key) => [
      ...ordered,
      ...data.filter(item => configurationPrefix(item.name) === key)
    ],
    []
  );
  const remaining = data.filter(item => !fixed.includes(item));
  return [...fixed, ...remaining];
};

export const configurations = async ({
  realm,
  pipelineId,
  environment,
  signal
}: ConfigurationsParams): Promise<Configuration[]> => {
  try {
    const { data } = await axiosInstance.get<Configuration[]>(
      `/design/realms/${encodeURIComponent(realm)}/pipelines/${encodeURIComponent(pipelineId)}/configurations/environment`,
      { params: { environment }, signal }
    );
    return orderConfigurations(data);
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
};

export type ReplicaInstance = {
  name: string;
};

type ReplicaInstanceParams = {
  realm: string;
  replicaName: string;
  signal?: AbortSignal;
};

export const replicaInstance = async ({
  realm,
  replicaName,
  signal
}: ReplicaInstanceParams): Promise<ReplicaInstance[]> => {
  try {
    const { data } = await axiosInstance.get<string[]>(
      `/design/realms/${encodeURIComponent(realm)}/replica/${encodeURIComponent(replicaName)}/instance/name`,
      { signal }
    );
    return data.map(name => ({ name }));
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
};

type PipelineEngineConfigParams = {
  realm: string;
  environment: string;
  signal?: AbortSignal;
};

type PipelineEngineConfigResponse = {
  deploy?: Record<string, { enable?: boolean } | undefined>;
};

export const pipelineEngineConfig = async ({
  realm,
  environment,
  signal
}: PipelineEngineConfigParams): Promise<boolean> => {
  try {
    const { data } = await axiosInstance.get<PipelineEngineConfigResponse>(
      `/design/realms/${encodeURIComponent(realm)}/pipelineEngineBetaVersion`,
      { params: { environment }, signal }
    );
    return !!data?.deploy?.[environment]?.enable;
  } catch (error) {
    throw toRuntimeServiceError(error);
  }
};
